import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ButtonDS,
  PredictiveSearch,
  TextInput,
  Typography,
} from "get-life-storybook-ts";
import {
  AnagraphicFieldT,
  codiceFiscaleMismatches,
  ProposalFailureKindT,
} from "../../../../../../native/proposalOutcome";
import { validateIdNumber } from "../../../../../../utils/validations";
import { GlobalProvinceApi } from "../../../../../../utils/InternationlChanges";
import type { AnagraphicFormI } from "./CheckoutPay.controller";

const LOCALE = process.env.REACT_APP_DOMAIN_LOCALE ?? "it";

interface ProposalFailureDialogPropsI {
  kind: ProposalFailureKindT;
  /** null mientras carga (o si no se pudo leer): el formulario espera a tenerlo. */
  anagraphic: AnagraphicFormI | null;
  translate: (key: string) => string;
  onSave: (form: AnagraphicFormI) => Promise<boolean>;
  onRetry: () => void;
  onClose: () => void;
  loadCities: () => Promise<unknown>;
}

/**
 * Aviso tras un FAILURE de la proposal AGL al aceptar la nota legal (native):
 * dice el motivo y deja corregir nombre, apellidos, codice fiscale y comune de
 * nacimiento y reintentar. Fecha de nacimiento y sexo no se editan aquí (cambian
 * la tarifa): si no cuadran, se pide contactar.
 */
const ProposalFailureDialog = ({
  kind,
  anagraphic,
  translate,
  onSave,
  onRetry,
  onClose,
  loadCities,
}: ProposalFailureDialogPropsI) => {
  const t = (key: string, fallback: string) => {
    const value = translate(key);
    return value === key ? fallback : value;
  };

  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<AnagraphicFormI | null>(anagraphic);
  const [fiscalCodeError, setFiscalCodeError] = useState<string | null>(null);
  const [cities, setCities] = useState<unknown[] | null>(null);

  useEffect(() => setForm(anagraphic), [anagraphic]);

  const bornInItaly = "IT" === form?.nationality;
  // El nomenclátor de comuni se pide una sola vez, al abrir el formulario.
  const citiesRequested = useRef(false);
  useEffect(() => {
    if (!editing || !bornInItaly || citiesRequested.current) return;
    citiesRequested.current = true;
    loadCities()
      .then((res) => setCities(((res as { answers?: unknown[] })?.answers ?? []) as unknown[]))
      .catch(() => setCities([]));
  }, [editing, bornInItaly, loadCities]);

  const mismatches = useMemo(
    () =>
      null === form
        ? []
        : codiceFiscaleMismatches({
            fiscalCode: form.idNumber,
            firstName: form.name,
            lastName: form.lastName,
            birthDate: form.birthDate,
            gender: form.gender,
          }),
    [form],
  );

  const hint = (field: AnagraphicFieldT): string =>
    ({
      lastName: t(
        "proposalFailure.mismatch.lastName",
        "Il cognome non corrisponde al codice fiscale.",
      ),
      firstName: t(
        "proposalFailure.mismatch.firstName",
        "Il nome non corrisponde al codice fiscale.",
      ),
      birthDate: t(
        "proposalFailure.mismatch.birthDate",
        "La data di nascita non corrisponde al codice fiscale: se è sbagliata, contattaci.",
      ),
      gender: t(
        "proposalFailure.mismatch.gender",
        "Il sesso non corrisponde al codice fiscale: contattaci.",
      ),
    })[field];

  const set = (field: keyof AnagraphicFormI) => (value: string) =>
    setForm((f) => (null === f ? f : { ...f, [field]: value ?? "" }));

  const save = async () => {
    if (null === form) return;
    const check = await validateIdNumber(LOCALE, form.idNumber, translate);
    if (check?.error) {
      setFiscalCodeError(check.message);
      return;
    }
    setFiscalCodeError(null);
    await onSave(form);
  };

  const canSave =
    null !== form &&
    "" !== form.name.trim() &&
    "" !== form.lastName.trim() &&
    "" !== form.idNumber.trim() &&
    "" !== (form.cityOfBirth ?? "").trim();

  return (
    <div className="checkoutPay__backdrop">
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="proposal-failure-title"
        data-testid="proposal-failure"
        className="checkoutPay__dialog"
      >
        <div className="checkoutPay__dialogText">
          <span id="proposal-failure-title">
            <Typography
              text={t(
                "proposalFailure.title",
                "Non è stato possibile completare la richiesta",
              )}
              variant="H3"
              weight="700"
              className="text-[#424242]"
            />
          </span>
          <Typography
            text={
              "FISCAL_CODE" === kind
                ? t(
                    "proposalFailure.fiscalCode",
                    "Il codice fiscale non corrisponde ai dati anagrafici (nome, cognome, data e luogo di nascita). Controlla i tuoi dati.",
                  )
                : t(
                    "proposalFailure.generic",
                    "La compagnia non ha accettato la richiesta. Controlla i tuoi dati e riprova; se il problema continua, contattaci.",
                  )
            }
            variant="BodyMedium"
            weight="400"
          />
          {mismatches.length > 0 && (
            <ul className="checkoutPay__mismatches" data-testid="proposal-failure-mismatches">
              {mismatches.map((m) => (
                <li key={m}>{hint(m)}</li>
              ))}
            </ul>
          )}
        </div>

        {editing && null !== form && (
          <div className="checkoutPay__form">
            <TextInput
              initialValue={form.name}
              label={t("proposalFailure.form.name", "Nome")}
              onValueChange={set("name")}
              validationChecks={[]}
            />
            <TextInput
              initialValue={form.lastName}
              label={t("proposalFailure.form.lastName", "Cognome")}
              onValueChange={set("lastName")}
              validationChecks={[]}
            />
            <TextInput
              initialValue={form.idNumber}
              label={t("proposalFailure.form.fiscalCode", "Codice fiscale")}
              onValueChange={set("idNumber")}
              validationChecks={[]}
              initialError={
                null === fiscalCodeError ? undefined : { error: true, message: fiscalCodeError }
              }
            />
            {bornInItaly && null !== cities ? (
              <PredictiveSearch
                initialValue={form.cityOfBirth}
                onValueChange={set("cityOfBirth")}
                options={cities as any}
                label={t("proposalFailure.form.cityOfBirth", "Comune di nascita")}
                sortOptionsByValue={true}
              />
            ) : (
              !bornInItaly && (
                <TextInput
                  initialValue={form.cityOfBirth}
                  label={t("proposalFailure.form.cityOfBirth", "Comune di nascita")}
                  onValueChange={set("cityOfBirth")}
                  validationChecks={[]}
                />
              )
            )}
          </div>
        )}

        <div className="checkoutPay__dialogButtons">
          {editing ? (
            <>
              <ButtonDS
                label={t("proposalFailure.saveAndRetry", "Salva e riprova")}
                onClick={save}
                disabled={!canSave}
                className="w-full"
              />
              <ButtonDS
                label={t("proposalFailure.cancel", "Annulla")}
                buttonType="secondary"
                onClick={() => setEditing(false)}
                className="w-full"
              />
            </>
          ) : (
            <>
              <ButtonDS
                label={t("proposalFailure.edit", "Modifica i tuoi dati")}
                onClick={() => setEditing(true)}
                disabled={null === form}
                className="w-full"
              />
              {"GENERIC" === kind && (
                <ButtonDS
                  label={t("proposalFailure.retry", "Riprova")}
                  buttonType="secondary"
                  onClick={onRetry}
                  className="w-full"
                />
              )}
              <ButtonDS
                label={t("proposalFailure.close", "Chiudi")}
                buttonType="tertiary"
                onClick={onClose}
                className="w-full"
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export const citiesCategory = (): string =>
  (GlobalProvinceApi as Record<string, string>)[LOCALE];

export default ProposalFailureDialog;
